<template>
  <aside class="conv-shell">
    <div class="conv-head">
      <button class="conv-new" @click="onNew" :disabled="creating">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.2" stroke-linecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        New requirement
      </button>
      <div class="conv-search">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input v-model="query" placeholder="Search requirements" />
      </div>
    </div>

    <div class="conv-list">
      <div v-for="g in groups" :key="g.key" class="conv-group">
        <div class="conv-group-head">
          <span>{{ g.label }}</span>
          <span class="conv-group-count">{{ g.items.length }}</span>
        </div>
        <button
          v-for="c in g.items"
          :key="c.id"
          class="conv-item"
          :class="{ active: store.activeId === c.id }"
          @click="store.selectConversation(c.id)"
        >
          <div class="conv-item-row">
            <StatusDot :status="c.status" />
            <span class="conv-item-title">{{ c.title }}</span>
          </div>
          <div class="conv-item-time">{{ relTime(c.update_time) }}</div>
        </button>
      </div>
      <div v-if="!groups.length && store.list.length" class="conv-empty">
        No requirements match "{{ query }}"
      </div>
      <div v-else-if="!store.list.length && !store.loadingList" class="conv-empty">
        No requirements yet. Start one above.
      </div>
      <div v-if="store.loadingList" class="conv-empty">Loading…</div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useConversationStore } from "@/stores/conversation";
import StatusDot from "@/components/design/StatusDot.vue";
import { STATUS_ORDER, STATUS_META } from "@/components/design/statusMeta";
import { relTime } from "@/lib/time";
import type { ConversationOut, ConversationStatus } from "@/types/api";

const store = useConversationStore();
const creating = ref(false);
const query = ref("");

onMounted(() => store.loadList());

const DEFAULT_TITLE = "New requirement";

const filtered = computed<ConversationOut[]>(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return store.list;
  return store.list.filter((c) => c.title.toLowerCase().includes(q));
});

const groups = computed(() => {
  const buckets = new Map<ConversationStatus, ConversationOut[]>();
  for (const s of STATUS_ORDER) buckets.set(s, []);
  for (const c of filtered.value) {
    const bucket = buckets.get(c.status as ConversationStatus);
    if (bucket) bucket.push(c);
  }
  return STATUS_ORDER.map((s) => ({
    key: s,
    label: STATUS_META[s].groupLabel,
    items: buckets.get(s) ?? [],
  })).filter((g) => g.items.length);
});

async function onNew() {
  creating.value = true;
  try {
    await store.startNew(DEFAULT_TITLE);
  } finally {
    creating.value = false;
  }
}
</script>

<style scoped>
.conv-shell {
  background: var(--paper-2);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}
.conv-head {
  padding: 14px 14px 10px;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}
.conv-new {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--ink);
  color: #fff;
  border: none;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, opacity 0.15s;
}
.conv-new:hover:not(:disabled) {
  background: #1a1f26;
}
.conv-new:active:not(:disabled) {
  transform: scale(0.98);
}
.conv-new:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.conv-search {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0 10px;
  color: var(--ink-4);
}
.conv-search input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 8px 8px 8px 10px;
  font-size: 12.5px;
  color: var(--ink);
}
.conv-search input::placeholder {
  color: var(--ink-4);
}
.conv-search:focus-within {
  border-color: var(--ink-3);
  background: #fff;
}
.conv-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 8px 16px;
  min-height: 0;
}
.conv-group {
  margin-top: 10px;
}
.conv-group-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px 4px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-4);
  font-weight: 600;
}
.conv-group-count {
  color: var(--ink-4);
  font-size: 10.5px;
  background: var(--border);
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 500;
}
.conv-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 2px;
  transition: background 0.12s, border-color 0.12s;
  font-family: inherit;
}
.conv-item:hover {
  background: var(--hover);
}
.conv-item.active {
  background: var(--surface);
  border-color: var(--border-2);
  box-shadow: 0 1px 2px rgba(11, 15, 20, 0.03);
}
.conv-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.conv-item-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
}
.conv-item-time {
  margin: 4px 0 0 16px;
  font-size: 10.5px;
  color: var(--ink-5);
}
.conv-empty {
  padding: 24px;
  color: var(--ink-4);
  font-size: 13px;
  text-align: center;
}
</style>
